import {
    ICurriculumItemableSearchResult,
    ISearchModel,
} from 'holberton-school-intranet-api';
import * as React from 'react';
import { ChangeEvent, ReactElement, useCallback, useEffect, useState } from 'react';

import Icon from '../../../common/Icon';
import { BuilderSearchItemRenderer } from '../BuilderSearch';

import BuilderSearchHits from './BuilderSearchHits';
import {
    buildCurriculumItemablesURI,
    fetchCurriculumItemables,
} from './curriculum-itemables-api';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

export interface IProps {
    csrfToken: string;
    renderItem: BuilderSearchItemRenderer;
    searchModels: ISearchModel[];
    searchURI: string;
}

export default function BuilderSearchLocal({
    csrfToken,
    renderItem,
    searchModels,
    searchURI,
}: IProps): ReactElement {
    const model = searchModels[0];
    const [query, setQuery] = useState('');
    const [items, setItems] = useState<ICurriculumItemableSearchResult[]>([]);
    const [loadMoreURI, setLoadMoreURI] = useState<string | undefined>();
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadPage = useCallback(
        async (uri: string, append: boolean): Promise<void> => {
            setError(null);
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            try {
                const page = await fetchCurriculumItemables(uri, csrfToken);
                setItems((current) =>
                    append ? current.concat(page.items) : page.items,
                );
                setLoadMoreURI(page.loadMoreURI);
            } catch (err) {
                setError(err.message);
                if (!append) {
                    setItems([]);
                    setLoadMoreURI(undefined);
                }
            }

            setLoading(false);
            setLoadingMore(false);
        },
        [csrfToken],
    );

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            const uri = buildCurriculumItemablesURI(searchURI, {
                limit: PAGE_SIZE,
                q: query || undefined,
            });
            loadPage(uri, false);
        }, query ? SEARCH_DEBOUNCE_MS : 0);

        return (): void => window.clearTimeout(timeout);
    }, [loadPage, query, searchURI]);

    const onLoadMore = (): void => {
        if (!loadMoreURI || loadingMore) {
            return;
        }
        loadPage(loadMoreURI, true);
    };

    const onChangeQuery = (e: ChangeEvent<HTMLInputElement>): void => {
        setQuery(e.currentTarget.value);
    };

    const onClearQuery = (): void => {
        setQuery('');
    };

    return (
        <div>
            <div className="list-group-item">
                <div
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        position: 'relative',
                    }}
                >
                    <input
                        className="form-control"
                        onChange={onChangeQuery}
                        placeholder="Search for projects..."
                        type="search"
                        value={query}
                    />
                    {query && (
                        <span
                            onClick={onClearQuery}
                            style={{
                                cursor: 'pointer',
                                position: 'absolute',
                                right: '5px',
                                zIndex: 3,
                            }}
                        >
                            <Icon
                                color="#9EB2B9"
                                fixedWidth
                                name="times-circle"
                            />
                        </span>
                    )}
                </div>
            </div>

            {error && (
                <div className="list-group-item text-danger">{error}</div>
            )}

            {loading && items.length === 0 && (
                <div className="list-group-item text-center">
                    <Icon name="spinner" spin />
                </div>
            )}

            {!loading && items.length === 0 && (
                <div className="list-group-item text-center text-warning">
                    No items match your criteria
                </div>
            )}

            {items.length > 0 && (
                <BuilderSearchHits
                    hasMore={loadMoreURI !== undefined}
                    items={items}
                    loadingMore={loadingMore}
                    model={model}
                    onLoadMore={onLoadMore}
                    renderItem={renderItem}
                />
            )}
        </div>
    );
}
